import { AlertTitle, Box, Paper, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import React, { useCallback } from 'react';

import { WIDTH_MODAL_COMPONENTS } from '../../../../../constants/addressBook';
import { PAGE_SIZE_OPTIONS } from '../../../../../constants/table';
import { IRow } from '../../../../../types/addressBook';
import NotificationAlert, { AlertDescription } from '../../../../_mui/_alerts_/NotificationAlert';
import { CenteredAlertWrapper } from '../../components';
import { ICsvRepairCopy } from '../../csvUploadProfile';

interface IProps {
  allIssuesFixed: boolean;
  noResultsForSearch: boolean;
  hasRows: boolean;
  searchQuery: string;
  filteredRows: IRow[];
  columns: GridColDef[];
  paginationModel: { page: number; pageSize: number };
  dataGridTableStyles: SxProps<Theme>;
  stickyColumnsStyles: SxProps<Theme>;
  focusedCellsStyles: SxProps<Theme>;
  editingCellsStyles: SxProps<Theme>;
  handleShowAllRows: () => void;
  clearModalState: () => void;
  handleProcessRowUpdate: (newRow: IRow) => IRow;
  columnWidths: Record<string, number>;
  copy: ICsvRepairCopy;
  isCellEditable?: (row: IRow, field: string) => boolean;
}

const RepairTableContent: React.FC<IProps> = ({
  allIssuesFixed,
  noResultsForSearch,
  hasRows,
  searchQuery,
  filteredRows,
  columns,
  paginationModel,
  dataGridTableStyles,
  stickyColumnsStyles,
  focusedCellsStyles,
  editingCellsStyles,
  handleShowAllRows,
  clearModalState,
  handleProcessRowUpdate,
  columnWidths,
  copy,
  isCellEditable,
}) => {
  const theme = useTheme();
  const apiRef = useGridApiRef();

  const resolveCellEditable = useCallback(
    (params: GridCellParams) => (isCellEditable ? isCellEditable(params.row as IRow, params.field) : true),
    [isCellEditable],
  );

  const handleCellClick = useCallback(
    (params: GridCellParams) => {
      if (params.isEditable && params.cellMode === 'view' && apiRef.current) {
        apiRef.current.startCellEditMode({
          id: params.id,
          field: params.field,
        });
      }
    },
    [apiRef],
  );

  const firstColumn = columns[0];
  const firstColumnId = firstColumn?.field ?? '';
  const firstColumnTitle = firstColumn?.headerName ?? '';

  const pinnedColumnHeadersStyle = {
    position: 'absolute',
    top: 0,
    height: 39,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    zIndex: 11,
    display: 'flex',
    alignItems: 'center',
  };

  switch (true) {
    case allIssuesFixed:
      return (
        <CenteredAlertWrapper>
          <NotificationAlert
            type={copy.repairSuccessAlert.type}
            actionButtonText={copy.repairSuccessAlert.buttonText}
            actionOnClick={handleShowAllRows}
          >
            <AlertTitle>{copy.repairSuccessAlert.title}</AlertTitle>
            <AlertDescription>{copy.repairSuccessAlert.description}</AlertDescription>
          </NotificationAlert>
        </CenteredAlertWrapper>
      );
    case noResultsForSearch:
      return (
        <CenteredAlertWrapper>
          <NotificationAlert
            type={copy.noResultsAlert.type}
            sx={{
              bgcolor: 'background.paper',
              mx: 'auto',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
            hideAlertIcon={true}
          >
            <AlertTitle>{copy.noResultsAlert.title(searchQuery)}</AlertTitle>
            <AlertDescription sx={{ mx: 'auto', maxWidth: WIDTH_MODAL_COMPONENTS }}>
              {copy.noResultsAlert.description}
            </AlertDescription>
          </NotificationAlert>
        </CenteredAlertWrapper>
      );
    case hasRows:
      return (
        <>
          <Paper
            sx={{
              position: 'relative',
              height: 437,
              borderRadius: 0.5,
            }}
          >
            <DataGrid
              apiRef={apiRef}
              key={filteredRows.length.toString()}
              rows={filteredRows}
              columns={columns}
              disableRowSelectionOnClick={true}
              initialState={{ pagination: { paginationModel } }}
              rowHeight={36}
              columnHeaderHeight={39}
              disableColumnMenu
              disableColumnSelector
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              sx={[dataGridTableStyles, stickyColumnsStyles, focusedCellsStyles, editingCellsStyles] as SxProps<Theme>}
              processRowUpdate={handleProcessRowUpdate}
              disableVirtualization
              isCellEditable={resolveCellEditable}
              onCellClick={handleCellClick}
            />
            <Box
              sx={{
                ...pinnedColumnHeadersStyle,
                right: 0,
                width: columnWidths.actions,
                borderLeft: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Box
              sx={{
                ...pinnedColumnHeadersStyle,
                left: 0,
                width: columnWidths[firstColumnId],
                borderRight: `1px solid ${theme.palette.divider}`,
                paddingLeft: `${columnWidths.status + 8}px`,
              }}
            >
              <Typography variant="body2" fontWeight="medium">
                {firstColumnTitle}
              </Typography>
            </Box>
          </Paper>
        </>
      );
    case !hasRows:
      return (
        <CenteredAlertWrapper>
          <NotificationAlert
            type={copy.noRowsFoundAlert.type}
            actionButtonText={copy.noRowsFoundAlert.buttonText}
            actionOnClick={clearModalState}
            sx={{ mr: 0 }}
          >
            <AlertTitle>{copy.noRowsFoundAlert.title}</AlertTitle>
            <AlertDescription>{copy.noRowsFoundAlert.description}</AlertDescription>
          </NotificationAlert>
        </CenteredAlertWrapper>
      );
    default:
      return null;
  }
};

export default RepairTableContent;
